import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, UserCircle, Download, Eye, EyeOff, Key } from 'lucide-react';
import { exportToExcel, formatUsersForExport } from '../utils/excelExport';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordResetDialog, setPasswordResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'manager',
    branch_id: '',
    assigned_branches: []
  });
  const [passwordResetForm, setPasswordResetForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Remove assigned_branches if not auditor or regional_manager
      if (formData.role !== 'auditor' && formData.role !== 'regional_manager') {
        delete payload.assigned_branches;
      }
      await api.post('/users', payload);
      toast.success('User created successfully');
      setDialogOpen(false);
      setFormData({
        username: '',
        password: '',
        role: 'manager',
        branch_id: '',
        assigned_branches: []
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleExport = () => {
    try {
      const formattedData = formatUsersForExport(users);
      exportToExcel(formattedData, 'users', 'Users');
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordResetForm.newPassword || !passwordResetForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordResetForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await api.patch(`/users/${selectedUser.id}/reset-password`, null, {
        params: { new_password: passwordResetForm.newPassword }
      });
      toast.success('Password reset successfully');
      setPasswordResetDialog(false);
      setPasswordResetForm({ newPassword: '', confirmPassword: '' });
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="user-management-title">
            User Management
          </h1>
          <p className="text-slate-600 mt-1">Manage branch managers and regional managers</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" className="gap-2" disabled={users.length === 0}>
            <Download size={16} />
            Export to Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white gap-2">
                <Plus size={16} />
                Add User
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  data-testid="user-username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, branch_id: '', assigned_branches: [] })} required>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="regional_manager">Regional Manager</SelectItem>
                    <SelectItem value="auditor">Auditor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(formData.role === 'auditor' || formData.role === 'regional_manager') ? (
                <div className="space-y-2">
                  <Label>Assign Branches (Multiple) *</Label>
                  <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                    {branches.map(branch => (
                      <label key={branch.id} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.assigned_branches.includes(branch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, assigned_branches: [...formData.assigned_branches, branch.id] });
                            } else {
                              setFormData({ ...formData, assigned_branches: formData.assigned_branches.filter(id => id !== branch.id) });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{branch.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    {formData.role === 'auditor' ? 'Auditor can view bills from selected branches only' : 'Regional Manager can manage bills from selected branches'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="branch">Branch *</Label>
                  <Select value={formData.branch_id} onValueChange={(value) => setFormData({ ...formData, branch_id: value })} required>
                    <SelectTrigger data-testid="user-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button data-testid="submit-user-btn" type="submit" className="w-full">
                Create User
              </Button>
            </form>
          </DialogContent>
          </Dialog>

          {/* Password Reset Dialog */}
          <Dialog open={passwordResetDialog} onOpenChange={setPasswordResetDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Reset Password - {selectedUser?.username}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password *</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordResetForm.newPassword}
                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, newPassword: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordResetForm.confirmPassword}
                    onChange={(e) => setPasswordResetForm({ ...passwordResetForm, confirmPassword: e.target.value })}
                    placeholder="Re-enter password"
                  />
                </div>
                <Button onClick={handlePasswordReset} className="w-full">
                  <Key size={16} className="mr-2" /> Reset Password
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No users found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {users.map((user) => (
                <Card key={user.id} data-testid={`user-card-${user.id}`} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-indigo-100 rounded-full">
                          <UserCircle className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{user.username}</h3>
                          <p className="text-sm text-slate-600">{user.role === 'regional_manager' ? 'Regional Manager' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
                          {user.branch_name && (
                            <p className="text-xs text-slate-500 mt-1">{user.branch_name}</p>
                          )}
                          {user.password_plain && user.password_plain !== 'N/A' && (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => setShowPasswords({ ...showPasswords, [user.id]: !showPasswords[user.id] })}
                                className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                              >
                                {showPasswords[user.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                                <span>Password:</span>
                              </button>
                              {showPasswords[user.id] && (
                                <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">
                                  {user.password_plain}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => {
                            setSelectedUser(user);
                            setPasswordResetDialog(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Key size={16} className="mr-1" /> Reset
                        </Button>
                        {user.role !== 'admin' && (
                          <Button
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => handleDelete(user.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
